import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import _ from "lodash";

import {
  ArrowForwardOutlinedIcon,
  AssistantOutlinedIcon,
  AutoAwesomeOutlinedIcon,
  CloseIcon,
  DescriptionOutlinedIcon,
  MoreVertIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { AI_DATA_EXTRACTION_ALLOWED_FILE_TYPES } from "utils/constants";
import { downloadFileWithFileName } from "utils/file";
import { handleInvalidRequest } from "utils/sdk";

import Box from "components/Box";
import Button from "components/Button";
import ButtonDropdown from "components/ButtonDropdown";
import GroupLabel from "components/ButtonDropdown/GroupLabel";
import Divider from "components/Divider";
import Drawer from "components/Drawer";
import IconButton from "components/IconButton";
import { LinearProgress } from "components/Loading";
import { MenuItem } from "components/Menu";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import SidepanelHeader from "components/SidepanelHeader";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast, { DEFAULT_ERROR_MESSAGE } from "components/Toast";
import Tooltip from "components/Tooltip";
import UploadButton from "components/UploadButton";
import { useAIMemoSection } from "components/useAIMemoSection";

import { ECompanySettingsFeatureType } from "entities/Company/components/CompanySettings/constants";
import { useCheckFeature } from "entities/Company/components/CompanySettings/utils";
import { useCompanySettings } from "entities/Company/sdk";
import { companyCustomFieldValueModifyAIDataExtractionSelection } from "entities/Deal/sdk";
import { getFileExtension } from "entities/Field/components/Filefield/FilesTable/utils";
import {
  deleteDueDiligenceFile,
  uploadFileToCustomField,
} from "entities/Field/sdk";
import { upload } from "entities/File/sdk";
import { handleInvalidMemoFieldRequest } from "entities/Memo/components/Fields/utils/utils";
import StatusTag from "entities/Memo/components/StatusTag";
import {
  IAIAnswerMemoSection,
  ISourceDocumentDetails,
  memoSectionAIProcessTrigger,
  sourceDocumentFileSignedUrlRetrieve,
  useMemoDealDetails,
} from "entities/Memo/sdk";
import { fieldUpdateNAStatusBulk } from "entities/Package/sdk";

export interface ISourceDocumentDrawer {
  open: boolean;
  memoName: string;
  onUpdate: () => void;
  isLoading: boolean;
  sourceDocument?: ISourceDocumentDetails;
}

const SourceDocumentDrawer = ({
  open,
  memoName,
  onUpdate,
  sourceDocument,
  isLoading,
}: ISourceDocumentDrawer) => {
  const [showLoading, setShowLoading] = useState(false);
  const [
    isModifyingFileValueSelectedForAIDataExtraction,
    setIsModifyingFileValueSelectedForAIDataExtraction,
  ] = useState(false);
  const [disableGenerateButtonOnClick, setDisableGenerateButtonOnClick] =
    useState<boolean>(false);
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();

  const dealId = useMemo(() => Number(params.dealId), [params.dealId]);
  const memoId = useMemo(() => Number(params.memoId), [params.memoId]);

  const navigate = useNavigate();
  const { data: companySettings } = useCompanySettings();

  const { mutate: mutateMemoDealDetails } = useMemoDealDetails({
    dealId,
    memoId,
  });

  const [singleSourceDocumentsSections, multiSourceDocumentsSections] =
    useMemo(() => {
      return _.partition(
        sourceDocument?.memo_sections || [],
        (section) => section.source_documents_count === 1
      );
    }, [sourceDocument?.memo_sections]);

  const isSectionValueMissing = useCallback(
    ({ section }: { section: IAIAnswerMemoSection }) => {
      const currentValue =
        _.first(section.due_diligence_section?.field)?.value || "";

      const isCurrentValueEmpty =
        _.isEmpty(currentValue) || EMPTY_VALUE_REGEX.test(currentValue);

      return isCurrentValueEmpty;
    },
    []
  );

  const allMemoSectionsHaveGeneratedValues = useMemo(
    () =>
      singleSourceDocumentsSections?.every(
        (section) => !isSectionValueMissing({ section }) || section.is_na
      ),
    [isSectionValueMissing, singleSourceDocumentsSections]
  );

  const isAnswerCurrentlyGenerating = useMemo(
    () =>
      singleSourceDocumentsSections?.every(
        (section) => section.ai_prompt.is_response_in_progress
      ),
    [singleSourceDocumentsSections]
  );

  const noFilesProvided = useMemo(
    () => _.isEmpty(sourceDocument?.files),
    [sourceDocument?.files]
  );

  const noFilesSelected = useMemo(
    () =>
      sourceDocument?.files.every(
        (file) => !file.is_file_value_selected_for_ai_data_extraction
      ),
    [sourceDocument?.files]
  );

  const sourceDocumentIsMarkedAsNA = useMemo(
    () => sourceDocument?.is_na,
    [sourceDocument?.is_na]
  );

  const disableGenerateAnswersButton = useMemo(
    () =>
      !_.isNil(singleSourceDocumentsSections) &&
      (allMemoSectionsHaveGeneratedValues ||
        noFilesProvided ||
        isAnswerCurrentlyGenerating ||
        disableGenerateButtonOnClick ||
        noFilesSelected ||
        sourceDocumentIsMarkedAsNA),
    [
      isAnswerCurrentlyGenerating,
      allMemoSectionsHaveGeneratedValues,
      noFilesProvided,
      singleSourceDocumentsSections,
      disableGenerateButtonOnClick,
      noFilesSelected,
      sourceDocumentIsMarkedAsNA,
    ]
  );

  const handleFileUpload = useCallback(
    ({ files }: { files: Array<File> }) => {
      if (!_.isNil(sourceDocument)) {
        setShowLoading(true);

        Promise.all(files.map((file) => upload({ file, isPrivate: true })))
          .then((fileIds) =>
            Promise.all(
              fileIds.map((fileId) =>
                uploadFileToCustomField({
                  dealId,
                  customFieldId: sourceDocument.id,
                  fileId,
                })
              )
            ).then(() => {
              toast.successMessage("The file(s) successfully uploaded!");
              onUpdate();
            })
          )
          .catch((error) => {
            if (!_.isUndefined(error)) {
              handleInvalidMemoFieldRequest({ error, dealId, navigate });
            } else {
              toast.errorMessage(DEFAULT_ERROR_MESSAGE);
            }
          })
          .finally(() => setShowLoading(false));
      }
    },
    [dealId, navigate, onUpdate, sourceDocument]
  );

  const handleFileModifyAIDataExtractionSelectionClick = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: ({
      customValueId,
      isFileValueSelectedForAIDataExtraction,
    }: {
      customValueId: number;
      isFileValueSelectedForAIDataExtraction: boolean;
    }) => {
      setIsModifyingFileValueSelectedForAIDataExtraction(true);

      companyCustomFieldValueModifyAIDataExtractionSelection({
        companyCustomFieldValueId: customValueId,
        isFileValueSelectedForAIDataExtraction,
      })
        .then(() => {
          onUpdate();
        })
        .catch(handleInvalidRequest)
        .finally(() => {
          setIsModifyingFileValueSelectedForAIDataExtraction(false);
        });
    },
  });

  const handleFileDelete = useCallback(
    ({
      customValueId,
      fileName,
    }: {
      customValueId: number;
      fileName: string;
    }) => {
      if (!_.isNil(sourceDocument)) {
        deleteDueDiligenceFile({
          dealId,
          customFieldId: sourceDocument.id,
          customValueId: customValueId,
        }).then(() => {
          toast.successMessage(`${fileName} file successfully deleted!`);
          onUpdate();
        });
      }
    },
    [dealId, onUpdate, sourceDocument]
  );

  const handleFileDownload = useCallback(
    ({ fileName, fileId }: { fileName: string; fileId: number }) => {
      if (!_.isNil(sourceDocument)) {
        sourceDocumentFileSignedUrlRetrieve({
          dealId,
          fileId,
          sourceDocumentId: sourceDocument?.id,
        })
          .then((fileUrl) =>
            downloadFileWithFileName({
              fileUrl,
              fileName,
            })
          )
          .catch(handleInvalidRequest);
      }
    },
    [dealId, sourceDocument]
  );
  const handleMemoSectionAIExtractionTrigger = useCallback(
    ({ memoSections }: { memoSections: Array<IAIAnswerMemoSection> }) => {
      setDisableGenerateButtonOnClick(true);
      const memoSectionIds = memoSections
        .filter((section) => !section.is_na)
        .map((section) => section.id);
      memoSectionAIProcessTrigger({
        memoId,
        dealId,
        memoSectionIds,
      })
        .then(() => {
          onUpdate();
          toast.successMessage(
            "The AI Extraction has started, you will be notified when that data has been processed!"
          );
        })
        .catch(handleInvalidMemoFieldRequest);
    },
    [memoId, dealId, onUpdate]
  );

  const {
    handleSelectedSourceDocumentId,
    handleEditDialogAIMemoSectionIdChange,
  } = useAIMemoSection();

  const onClose = useCallback(() => {
    onUpdate();
    handleSelectedSourceDocumentId({ value: null });
  }, [onUpdate, handleSelectedSourceDocumentId]);

  const tooltipTitle = useMemo(() => {
    if (sourceDocumentIsMarkedAsNA)
      return "The source document has been marked as N/A. Please revoke the N/A status in order to generate an answer.";
    if (noFilesProvided)
      return "No files have been provided for the summarization.";
    if (noFilesSelected)
      return "No files have been selected for the summarization.";
    if (allMemoSectionsHaveGeneratedValues)
      return "There are no sections available for AI summarization.";
    if (isAnswerCurrentlyGenerating)
      return "Answers are currently being generated...";
    return undefined;
  }, [
    allMemoSectionsHaveGeneratedValues,
    noFilesSelected,
    noFilesProvided,
    isAnswerCurrentlyGenerating,
    sourceDocumentIsMarkedAsNA,
  ]);

  const sectionTooltipText = useCallback(
    ({ section }: { section: IAIAnswerMemoSection }) => {
      if (section.is_na) {
        return "This section has been marked as NA";
      }
      if (section.ai_prompt.is_response_in_progress) {
        return "An answer is currently being generated...";
      }
      return undefined;
    },
    []
  );

  const handleMemoSectionClick = useCallback(
    ({ section }: { section: IAIAnswerMemoSection }) => {
      if (
        !section.is_na &&
        !section.ai_prompt.is_response_in_progress &&
        !isLoading
      ) {
        onClose();
        mutateMemoDealDetails();
        handleEditDialogAIMemoSectionIdChange({
          sectionId: section.id,
        });
      }
    },
    [
      handleEditDialogAIMemoSectionIdChange,
      mutateMemoDealDetails,
      onClose,
      isLoading,
    ]
  );

  const handleMarkAsNA = useCallback(() => {
    if (!_.isNil(sourceDocument)) {
      fieldUpdateNAStatusBulk({
        dealId,
        fieldIds: [sourceDocument.id],
        naStatus: !sourceDocument.is_na,
      })
        .then(() => {
          onUpdate();
          toast.successMessage(
            `The field was successfully ${
              !sourceDocument.is_na ? "disabled" : "enabled"
            }!`
          );
        })
        .catch(handleInvalidRequest);
    }
  }, [dealId, onUpdate, sourceDocument]);

  return (
    <Drawer open={open} anchor="right" onClose={onClose}>
      <SidepanelHeader>
        <Stack direction="column">
          <Text fontWeight={500} fontSize={11} color={colors.blue100}>
            AI SOURCE DCOUMENT
          </Text>

          <Stack direction="row" alignItems="center" gap={1.2} width="765px">
            <AutoAwesomeOutlinedIcon />
            {isLoading ? (
              <Skeleton variant="rectangular" width={200} height={39} />
            ) : (
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Text variant="h2">{sourceDocument?.name}</Text>
                {noFilesProvided && !sourceDocumentIsMarkedAsNA && (
                  <StatusTag text="missing" backgroundColor={colors.red40} />
                )}
                {sourceDocumentIsMarkedAsNA && (
                  <StatusTag text="N/A" backgroundColor={colors.gray20} />
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </SidepanelHeader>
      <Box
        sx={{
          flex: 1,
          padding: theme.spacing(3, 5, 5, 5),
        }}
      >
        {!sourceDocumentIsMarkedAsNA ? (
          <Stack marginBottom={3}>
            <Text variant="labelSmall" marginBottom={1.5}>
              uploaded files
            </Text>
            <Divider
              sx={{
                color: colors.blue80,
                width: "100%",
              }}
            />
            {isLoading &&
              _.range(2).map((index) => (
                <Stack key={index}>
                  <Stack padding={theme.spacing(0.5, 0)}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Skeleton variant="text" width={350} height={33} />
                      <Skeleton variant="text" width={50} height={27} />
                    </Stack>
                    <Divider
                      sx={{
                        color: colors.blue80,
                        width: "100%",
                      }}
                    />
                  </Stack>
                </Stack>
              ))}
            {sourceDocument?.files.map((file) => {
              const fileCanBeUsedForAIDataExtraction =
                !_.isNil(file) &&
                Object.keys(AI_DATA_EXTRACTION_ALLOWED_FILE_TYPES).includes(
                  file.file_type
                ) &&
                Object.values(AI_DATA_EXTRACTION_ALLOWED_FILE_TYPES)
                  .flatMap((item) => item)
                  .includes(getFileExtension({ fileName: file.file_name }));

              return (
                <Stack padding={theme.spacing(0.5, 0)}>
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" gap={1} alignItems="center">
                      <DescriptionOutlinedIcon
                        sx={{
                          width: "20px",
                          height: "20px",
                          color: colors.gray60,
                        }}
                      />
                      <Text>{file.original_file_name}</Text>
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1}
                      padding={theme.spacing(1, 0)}
                    >
                      {companySettings?.onboarding_company_package_enabled &&
                        fileCanBeUsedForAIDataExtraction && (
                          <Tooltip
                            title={
                              file.is_file_value_selected_for_ai_data_extraction
                                ? "Unmark as AI source file."
                                : "Mark as AI source file."
                            }
                          >
                            <IconButton
                              size="small"
                              loading={
                                isModifyingFileValueSelectedForAIDataExtraction
                              }
                              onClick={() =>
                                handleFileModifyAIDataExtractionSelectionClick({
                                  customValueId: file.custom_field_value_id,
                                  isFileValueSelectedForAIDataExtraction:
                                    !file.is_file_value_selected_for_ai_data_extraction,
                                })
                              }
                              sx={
                                file.is_file_value_selected_for_ai_data_extraction
                                  ? { color: colors.blue100, opacity: 1 }
                                  : {}
                              }
                            >
                              <AssistantOutlinedIcon
                                sx={{
                                  width: "18px",
                                  height: "18px",
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      <ButtonDropdown
                        icon={<MoreVertIcon />}
                        label=""
                        variant="text"
                        size="small"
                        width="auto"
                        sx={{
                          padding: theme.spacing(0.5, 0.75),
                        }}
                      >
                        <GroupLabel label="Actions" />
                        <MenuItem
                          onClick={() =>
                            handleFileDownload({
                              fileName: file.original_file_name,
                              fileId: file.id,
                            })
                          }
                        >
                          Download file
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            handleFileDelete({
                              customValueId: file.custom_field_value_id,
                              fileName: file.original_file_name,
                            })
                          }
                        >
                          <Text variant="text2">Delete file</Text>
                        </MenuItem>
                      </ButtonDropdown>
                    </Stack>
                  </Stack>
                  <Divider
                    sx={{
                      color: colors.blue80,
                      width: "100%",
                    }}
                  />
                </Stack>
              );
            })}
            {_.isEmpty(sourceDocument?.files) && !isLoading && (
              <Text
                fontSize={13}
                fontWeight={400}
                sx={{
                  marginTop: 1,
                  color: colors.gray60,
                }}
              >
                No files have been uploaded to this source document
              </Text>
            )}
            {showLoading && (
              <LinearProgress sx={{ position: "sticky", left: 0 }} />
            )}
            <UploadButton
              onChange={handleFileUpload}
              renderComponent={() => (
                <Button
                  color="secondary"
                  sx={{
                    marginTop: 1,
                  }}
                >
                  <Text
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: colors.blue100,
                    }}
                  >
                    + Upload new file
                  </Text>
                </Button>
              )}
              style={{ width: "fit-content" }}
            />
            {noFilesSelected && !noFilesProvided && (
              <Text
                variant="text4"
                color={colors.red100}
                fontWeight={500}
                sx={{ wordBreak: "break-word" }}
                width="750px"
                marginTop={2}
              >
                Please select at least one file to be used as an AI source for
                this document type. Only .pdf, .xlsx, and .docx files may be
                used as AI sources.
              </Text>
            )}
          </Stack>
        ) : (
          <Stack direction="column" gap={1} marginBottom={3}>
            <Text
              variant="text4"
              color={colors.red100}
              fontWeight={500}
              sx={{ wordBreak: "break-word" }}
              width="750px"
            >
              This source document has been marked as N/A. In order to generate
              answers, please revoke the N/A status.
            </Text>
            <Button
              color="secondary"
              sx={{ width: "fit-content" }}
              onClick={handleMarkAsNA}
            >
              Revoke N/A status
            </Button>
          </Stack>
        )}
        <Text variant="labelSmall" marginBottom={1.5}>
          SECTIONS USING THIS SOURCE DOCUMENT
        </Text>
        <Divider
          sx={{
            color: colors.blue80,
            width: "100%",
          }}
        />
        {isLoading &&
          _.range(2).map((index) => (
            <Stack key={index} padding={theme.spacing(0.5, 0)}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Skeleton variant="text" width={350} height={33} />
                <Skeleton variant="text" width={100} height={27} />
              </Stack>
              <Divider
                sx={{
                  color: colors.blue80,
                  width: "100%",
                }}
              />
            </Stack>
          ))}
        {singleSourceDocumentsSections?.map((section) => (
          <Stack
            key={`${section.id}-${section.is_na}`}
            padding={theme.spacing(0.5, 0)}
          >
            {!_.isNil(sourceDocument) && (
              <Tooltip title={sectionTooltipText({ section })}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    cursor:
                      section.is_na || section.ai_prompt.is_response_in_progress
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={() => handleMemoSectionClick({ section })}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <AssistantOutlinedIcon
                      sx={{
                        width: "20px",
                        height: "20px",
                        color: colors.gray60,
                      }}
                    />
                    <Text>
                      {memoName} {">"} {section.title}
                    </Text>
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    padding={theme.spacing(1, 0)}
                  >
                    {isSectionValueMissing({ section }) ? (
                      <StatusTag
                        text="missing"
                        backgroundColor={colors.red40}
                      />
                    ) : (
                      <StatusTag
                        text="edited"
                        backgroundColor={colors.teal20}
                      />
                    )}
                    <ArrowForwardOutlinedIcon
                      sx={{
                        width: "16px",
                        height: "16px",
                        color: colors.gray60,
                      }}
                    />
                  </Stack>
                </Stack>
              </Tooltip>
            )}
            <Divider
              sx={{
                color: colors.blue80,
                width: "100%",
              }}
            />
          </Stack>
        ))}
        <Text
          fontSize={13}
          fontWeight={400}
          sx={{
            marginTop: 1,
            color: colors.gray60,
            marginBottom: 1.5,
          }}
        >
          {_.isEmpty(singleSourceDocumentsSections)
            ? "There are currently no available sections"
            : "Answers will be generated to all non-edited sections. To override an edited section, go directly to that section."}
        </Text>
        {!_.isEmpty(singleSourceDocumentsSections) && (
          <Tooltip title={tooltipTitle}>
            <span
              style={{
                cursor: disableGenerateAnswersButton
                  ? "not-allowed"
                  : "pointer",
              }}
            >
              <Button
                color="secondary"
                disabled={disableGenerateAnswersButton}
                sx={{
                  transition: "opacity 0.2s ease-in-out",
                  opacity: disableGenerateAnswersButton ? 0.5 : 1,
                  padding: 1,
                }}
                onClick={() => {
                  handleMemoSectionAIExtractionTrigger({
                    memoSections:
                      singleSourceDocumentsSections?.filter(
                        (section) =>
                          isSectionValueMissing({ section }) && !section.is_na
                      ) || [],
                  });
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  color={
                    disableGenerateAnswersButton
                      ? colors.gray80
                      : colors.blue100
                  }
                >
                  <AutoAwesomeOutlinedIcon
                    sx={{
                      width: "16px",
                      height: "16px",
                      fontWeight: 500,
                      transition: "color 0.2s ease-in-out",
                    }}
                  />
                  <Text
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      transition: "color 0.2s ease-in-out",
                    }}
                  >
                    Generate for all empty sections
                  </Text>
                </Stack>
              </Button>
            </span>
          </Tooltip>
        )}
        <Text variant="labelSmall" marginBottom={1.5} marginTop={5}>
          SECTIONS USING THIS AND OTHER SOURCE DOCUMENTS
        </Text>
        <Divider
          sx={{
            color: colors.blue80,
            width: "100%",
          }}
        />
        {isLoading &&
          _.range(2).map((index) => (
            <Stack key={index}>
              <Stack padding={theme.spacing(0.5, 0)}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Skeleton variant="text" width={350} height={33} />
                  <Skeleton variant="text" width={100} height={27} />
                </Stack>
                <Divider
                  sx={{
                    color: colors.blue80,
                    width: "100%",
                  }}
                />
              </Stack>
            </Stack>
          ))}
        {multiSourceDocumentsSections?.map(
          (section) =>
            !_.isNil(sourceDocument) && (
              <Tooltip title={sectionTooltipText({ section })}>
                <Stack padding={theme.spacing(0.5, 0)}>
                  {!_.isNil(sourceDocument) && (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        cursor:
                          section.is_na ||
                          section.ai_prompt.is_response_in_progress
                            ? "not-allowed"
                            : "pointer",
                      }}
                      onClick={() => handleMemoSectionClick({ section })}
                    >
                      <Stack direction="row" gap={1} alignItems="center">
                        <AssistantOutlinedIcon
                          sx={{
                            width: "20px",
                            height: "20px",
                            color: colors.gray60,
                          }}
                        />
                        <Text>
                          {memoName} {">"} {section.title}
                        </Text>
                      </Stack>
                      <Stack
                        direction="row"
                        gap={1}
                        padding={theme.spacing(1, 0)}
                        alignItems="center"
                      >
                        {isSectionValueMissing({ section }) ? (
                          <StatusTag
                            text="missing"
                            backgroundColor={colors.red40}
                          />
                        ) : (
                          <StatusTag
                            text="edited"
                            backgroundColor={colors.teal20}
                          />
                        )}
                        <ArrowForwardOutlinedIcon
                          sx={{
                            width: "16px",
                            height: "16px",
                            color: colors.gray60,
                          }}
                        />
                      </Stack>
                    </Stack>
                  )}
                  <Divider
                    sx={{
                      color: colors.blue80,
                      width: "100%",
                    }}
                  />
                </Stack>
              </Tooltip>
            )
        )}
        {_.isEmpty(multiSourceDocumentsSections) ? (
          <Text
            fontSize={13}
            fontWeight={400}
            sx={{
              marginTop: 1,
              color: colors.gray60,
            }}
          >
            There are currently no available sections
          </Text>
        ) : (
          <Text
            fontSize={13}
            fontWeight={400}
            sx={{
              marginTop: 1,
              color: colors.gray60,
            }}
          >
            These answers are created from multiple source documents. Please
            upload all required documents and go to the specific sections to
            generate them.
          </Text>
        )}
      </Box>
    </Drawer>
  );
};

export default SourceDocumentDrawer;
